import React, { useState } from 'react';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { CustomizedTabs } from 'components/common/CustomizedTabs/CustomizedTabs';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TableFooter } from 'components/Dashboards/Project/Components/VersionCompare/TableFotter/TableFooter';
import classes from 'components/Dashboards/Project/Components/VersionCompare/style/Table.module.scss';
import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { smallPageSize } from 'components/Dashboards/Project/Components/VersionCompare/utils/table.utils';
import { projectConfig } from 'components/Dashboards/Project/ProjectWrapper/projectConfig';
import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import { useStatusChangesLogic } from 'components/widgets/TopStatusChanges/StatusChanges.logic';
import { useStatusChangesProvider } from 'components/widgets/TopStatusChanges/StatusChanges.provider';
import { LocalFilters } from 'components/widgets/TopStatusChanges/localFilters/LocalFilters';
import { PeriodItemEntry } from 'components/widgets/TopStatusChanges/queries/useQueryStatusChangesTableData';
import { columnsV8, initialState } from 'components/widgets/TopStatusChanges/statusChanges.utils';

const elementId = 'status-changes';
const id = 'topStatusChanges';
const componentKey = 'topStatusChanges';
const title = 'Top Status Changes';

const StatusChanges = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    isSinglePage = false,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
}: ITableWidgetWrapperProps) => {
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    const { ui, api } = useStatusChangesProvider({
        widgetId,
        globalFilterData,
        localFilters,
        contractId,
        latestVersionId,
        projectId,
        compareVersionId,
        table,
    });

    const { logic } = useStatusChangesLogic({ ui, api, table, title, subTitle, isSinglePage });

    return (
        <WidgetWithTitle
            title={title}
            tooltip={api.getStatusChangesTableData.data?.info}
            id={id}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.isInPinboard && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ) : (
                    <LocalFilters
                        key={'localFilters'}
                        status={ui.state.status}
                        setStatus={ui.setters.setStatus}
                        isCriticalPath={ui.state.isCriticalPath}
                        setIsCriticalPath={ui.setters.setIsCriticalPath}
                    />
                ),
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => table.setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    elementId={elementId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    callBack={table.setWidgetColumns}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown
                    title={title}
                    key={'export'}
                    subTitle={subTitle}
                    handleAPIExcelExport={logic.handleAPIExcelExport}
                />,
            ]}
        >
            {logic.filteredData && (
                <div className={classes.tableWrapperContainer}>
                    {logic.tabsArrData.length > 0 && (
                        <CustomizedTabs<TKeys>
                            tabs={logic.tabsArrData}
                            activeTab={ui.state.activeTab}
                            setActiveTab={ui.setters.setActiveTab}
                        />
                    )}

                    {table.updatedInitialState && (
                        <TableV8<PeriodItemEntry>
                            data={logic.filteredData}
                            columns={columnsV8}
                            isLoading={
                                api.getStatusChangesTableData.isLoading || api.getStatusChangesTableData.isFetching
                            }
                            noData={<NoData>No Changes To Display</NoData>}
                            sorting={table.sorting}
                            setSorting={table.setSorting}
                            columnVisibility={table.columnVisibility}
                            setColumnVisibility={table.setColumnVisibility}
                            columnOrder={table.columnOrder}
                            setColumnOrder={table.setColumnOrder}
                            manualPagination={false}
                            manualSorting={false}
                            pagination={table.pagination}
                            rowSelection={table.rowSelection}
                            setRowSelection={table.setRowSelection}
                        />
                    )}

                    {logic.filteredData.length > smallPageSize && !isSinglePage && (
                        <TableFooter
                            setPageSize={logic.setPageSize}
                            pageSize={logic.pageSize}
                            link={!widgetId ? projectConfig.statusChanges.link : undefined}
                        />
                    )}
                </div>
            )}
            <CustomTableColumnsModal
                onHideModal={() => table.setModalColumnsOpen(false)}
                showModal={table.modalColumnsOpen}
                widgetId={widgetId}
                componentKey={componentKey}
                tableInstance={table.tableInstance}
                initialState={initialState}
                columns={columnsV8}
                onChangeOrder={table.setColumnOrder}
                onChangeVisibility={table.setColumnVisibility}
            />
            {logic.spinnerOpen && <OverlayWithSpinner />}
        </WidgetWithTitle>
    );
};
export default React.memo(StatusChanges);
